/**
 * Utilitaires pour la gestion de l'historique des recherches
 * @author ANS
 * @version 1.0.0
 */

/**
 * Classe utilitaire pour gérer l'historique des recherches
 */
export class HistoryUtils {
  
  /**
   * Clé de stockage local pour l'historique
   */
  static STORAGE_KEY = 'ans-smt-search-history';

  /**
   * Sauvegarde un élément dans l'historique des recherches
   * @param {Object} searchItem - L'élément de recherche à sauvegarder
   * @param {string} searchItem.query - La requête de recherche
   * @param {boolean} searchItem.advanced - Type de recherche (avancée ou non)
   * @param {Array<string>} searchItem.languages - Langues utilisées
   * @param {Array<string>} searchItem.types - Types de recherche
   * @param {number} searchItem.conceptsCount - Nombre de concepts trouvés
   * @param {number} searchItem.terminologiesCount - Nombre de terminologies trouvées
   * @returns {boolean} True si la sauvegarde a réussi
   */
  static saveToHistory(searchItem) {
    try {
      const history = this.getHistory();
      
      // Créer un nouvel élément avec la date actuelle
      const newItem = {
        ...searchItem,
        date: new Date(),
        id: this.generateId()
      };

      // Chaque recherche est journalisée séparément, même si identique à une recherche précédente
      history.unshift(newItem);

      // Sauvegarder dans le localStorage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(history));
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de l\'historique:', error);
      return false;
    }
  }

  /**
   * Récupère l'historique des recherches depuis le localStorage
   * @returns {Array} Liste des éléments de l'historique
   */
  static getHistory() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      
      const history = JSON.parse(stored);
      
      // Convertir les dates string en objets Date
      return history.map(item => ({
        ...item,
        date: new Date(item.date)
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique:', error);
      return [];
    }
  }

  /**
   * Supprime un élément de l'historique par son ID
   * @param {string} id - L'ID de l'élément à supprimer
   * @returns {boolean} True si la suppression a réussi
   */
  static removeFromHistory(id) {
    try {
      const history = this.getHistory();
      const filteredHistory = history.filter(item => item.id !== id);
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredHistory));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'élément:', error);
      return false;
    }
  }

  /**
   * Vide complètement l'historique des recherches
   * @returns {boolean} True si la suppression a réussi
   */
  static clearHistory() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'historique:', error);
      return false;
    }
  }

  /**
   * Met à jour le nombre de concepts trouvés pour la dernière recherche
   * @param {number} conceptsCount - Nombre de concepts trouvés
   * @returns {boolean} True si la mise à jour a réussi
   */
  static updateLastSearchConcepts(conceptsCount) {
    try {
      const history = this.getHistory();
      
      if (history.length === 0) {
        return false;
      }

      // Récupérer le dernier élément (le plus récent)
      const lastSearch = history[0];
      
      // Vérifier si le nombre de concepts a déjà été mis à jour
      const hasConceptsResult = lastSearch.conceptsCount !== undefined && lastSearch.conceptsCount !== null;
      
      if (hasConceptsResult) {
        return false;
      }
      
      // Vérifier que le type "concepts" est autorisé pour cette recherche
      const searchTypes = lastSearch.types || [];
      const hasConceptsType = searchTypes.includes('concept');
      
      if (!hasConceptsType) {
        return false;
      }

      // Mettre à jour le nombre de concepts
      lastSearch.conceptsCount = conceptsCount;

      // Sauvegarder l'historique mis à jour
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(history));
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du nombre de concepts:', error);
      return false;
    }
  }

  /**
   * Met à jour le nombre de terminologies trouvées pour la dernière recherche
   * @param {number} terminologiesCount - Nombre de terminologies trouvées
   * @returns {boolean} True si la mise à jour a réussi
   */
  static updateLastSearchTerminologies(terminologiesCount) {
    try {
      const history = this.getHistory();
      
      if (history.length === 0) {
        return false;
      }

      // Récupérer le dernier élément (le plus récent)
      const lastSearch = history[0];
      
      // Vérifier si le nombre de terminologies a déjà été mis à jour
      const hasTerminologiesResult = lastSearch.terminologiesCount !== undefined && lastSearch.terminologiesCount !== null;
      
      if (hasTerminologiesResult) {
        return false;
      }
      
      // Vérifier que le type "termino" est autorisé pour cette recherche
      const searchTypes = lastSearch.types || [];
      const hasTerminoType = searchTypes.includes('termino');
      
      if (!hasTerminoType) {
        return false;
      }

      // Mettre à jour le nombre de terminologies
      lastSearch.terminologiesCount = terminologiesCount;

      // Sauvegarder l'historique mis à jour
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(history));
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du nombre de terminologies:', error);
      return false;
    }
  }

  /**
   * Génère un ID unique pour un élément d'historique
   * @returns {string} ID unique
   */
  static generateId() {
    // Utiliser crypto.getRandomValues() pour une génération plus sécurisée
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return Date.now().toString(36) + array[0].toString(36);
  }
}

/**
 * Fonctions utilitaires exportées individuellement pour une utilisation plus simple
 */

/**
 * Sauvegarde une recherche dans l'historique
 * @param {Object} searchData - Données de la recherche
 * @returns {boolean} Succès de l'opération
 */
export const saveSearch = (searchData) => HistoryUtils.saveToHistory(searchData);

/**
 * Récupère l'historique des recherches
 * @returns {Array} Liste de l'historique
 */
export const getSearchHistory = () => HistoryUtils.getHistory();

/**
 * Supprime tout l'historique
 * @returns {boolean} Succès de l'opération
 */
export const clearSearchHistory = () => HistoryUtils.clearHistory();


/**
 * Met à jour le nombre de concepts de la dernière recherche
 * @param {number} conceptsCount - Nombre de concepts
 * @returns {boolean} Succès de l'opération
 */
export const updateLastSearchConcepts = (conceptsCount) => 
  HistoryUtils.updateLastSearchConcepts(conceptsCount);

/**
 * Met à jour le nombre de terminologies de la dernière recherche
 * @param {number} terminologiesCount - Nombre de terminologies
 * @returns {boolean} Succès de l'opération
 */
export const updateLastSearchTerminologies = (terminologiesCount) => 
  HistoryUtils.updateLastSearchTerminologies(terminologiesCount);

/**
 * Export par défaut de la classe principale
 */
export default HistoryUtils;